import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { LabelModule } from 'primeng/label';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Subscription } from 'rxjs';
import { DepartmentSelectComponent } from '../../components/department-select/department-select.component';
import { OrganizationSelectComponent } from '../../components/organization-select/organization-select.component';
import { RoleSelectComponent } from '../../components/role-select/role-select.component';
import { Role } from '../../enums/role';
import { UserOrganization } from '../../models/organization';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Functions } from '../../utilities/functions';
import { Parameters } from '../../utilities/parameters';
import { Reset } from '../../utilities/reset';


@Component({
  selector: 'app-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    TabsModule,
    ToggleSwitchModule,
    ButtonModule,
    SelectButtonModule,
    TranslatePipe,
    LabelModule,
    OrganizationSelectComponent,
    DepartmentSelectComponent,
    RoleSelectComponent
  ],
  templateUrl: './user-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class UserDialogComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private langSubscription!: Subscription

  @Input() isNew = false;
  @Input() messageService!: UiNotificationService;

  // visibilità dialog (bidirezionale)
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // utente
  @Input() user: User = Reset.user();

  // evento finale
  @Output() saved = new EventEmitter<User>();

  tabsId: string = '0';
  isSuperAdmin: boolean = false;

  roleOptions: any[] = [];
  selectedRoles: any[] = [];

  stateOptions: any[] = [];

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) {
    this.isSuperAdmin = Parameters.loggedUser.roles.includes(Role.superAdmin);
  }

  ngOnChanges() {
    this.tabsId = '0';
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  ngOnInit(): void  {
    this.tabsId = '0';

    const maxRole = Functions.getMaxRoleUsable(Parameters.loggedUser.roles);
    const rawRoles = Functions.getRoles().filter(r => r.id >= maxRole);
    this.roleOptions = rawRoles.map(role => ({
      ...role,
      // Creiamo una proprietà 'translatedLabel' già localizzata
      label: this.translate.instant(role.value)
    }));

    this.langSubscription = this.translate.stream(['no', 'yes']).subscribe(translations => {
      this.stateOptions = [
        { label: translations['no'], value: false },
        { label: translations['yes'], value: true }
      ];
    });
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // Questa funzione gestisce l'evento (itemsChange)="user.organizations = $event"
  // ma ci permette di intercettare quale organizzazione è stata attivata
  onOrganizationsChange(newOrganizations: UserOrganization[]) {
    // 1. Identifica l'organizzazione che è appena stata attivata (associated diventato true)
    // confrontando il nuovo array con quello attualmente salvato nello stato
    const org = newOrganizations.find(o => o.associated && o.departments.length === 0);

    // 2. Aggiorna il riferimento principale per far scattare l'ngOnChanges sui componenti figli
    this.user.organizations = newOrganizations;

    // 3. Se abbiamo trovato un'organizzazione appena attivata, scarichiamo i dipartimenti
    if (org) {
      const orgId = org.organizationId;
      this.apiService.getOrganizationDepartments(orgId).subscribe({
        next: (departments) => {
          // Aggiorna l'organizzazione specifica inserendo i dipartimenti scaricati
          this.user.organizations = this.user.organizations.map(org => {
            if (org.organizationId === orgId) {
              return { ...org, departments: departments };
            }
            return org;
          });
        
          // NOTA: Avendo riassegnato l'intero array tramite .map(), Angular genererà 
          // un nuovo riferimento di memoria. Questo farà scattare nuovamente l'ngOnChanges 
          // di app-department-select che vedrà finalmente i dipartimenti popolati!
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      });
    }
  }

  onResendEMail(event: Event): void {
    this.messageService.confirm('messages.emailLink',
      () => {
        this.apiService.resendEmail(this.user).subscribe({
          next: () => {
            this.messageService.showSuccess('messages.success')
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        });
      },
      { labelKey: 'send', severity:'primary' }, 'confirm', 'pi-send'
    );
  }

  onResetPassword(event: Event): void {
    this.messageService.confirm('password.reset',
      () => {
        this.apiService.resetPassword(this.user).subscribe({
          next: () => {
            this.messageService.showSuccess('messages.success')
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        });
      },
      { labelKey: 'reset', severity: 'primary' }, 'confirm', 'pi-key'
    );
  }

  onSave() {
    this.user.organizations = this.user.organizations.filter(o => o.associated);
    if (this.isNew) {
      this.apiService.addUser(this.user).subscribe({
        next: (id) => {
          if (id === '' || id === null) {
            this.savedCompleted();
            this.messageService.showSuccess('messages.dataSaved');
          } else {
            this.user.userId = id
            this.updateUserOrganizations();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      });
    } else {
      this.apiService.updateUser(this.user).subscribe({
        next: () => {
          this.savedCompleted();
          this.messageService.showSuccess('messages.dataSaved');
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      });
    }
  }

  private savedCompleted() {
    this.saved.emit(this.user);
    this.onClose();
  }

  private updateUserOrganizations() {
    this.messageService.confirm('messages.userAssociated',
      () => {
        this.apiService.linkUser(this.user).subscribe({
          next: () => {
            this.messageService.showSuccess('messages.success')
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        });
      },
      { labelKey: 'associate', severity: 'primary' }, 'user', 'pi-user-edit',
      { name: this.user.userName }
    );
  }
}
